import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { X, ZoomIn, ChevronLeft, ChevronRight, Loader2, RotateCw, RotateCcw, Paperclip, Trash2, FileText } from 'lucide-react';

const pdfMi = (s) => (s?.content_type || '').includes('pdf')
  || (s?.dosya_adi || '').toLowerCase().endsWith('.pdf');

/**
 * Faturanın belge sayfaları — detay ve DÜZENLEME ekranının ortak bileşeni.
 *
 * Sayfalar GridFS'te, faturaya bağlı bir dosya kutusu belgesinde durur
 * (kutudan işlenen faturada o belge; sonradan eklenende sunucunun açtığı
 * belge). Kutudan geçmemiş eski faturalarda `image_data` YEDEK olarak
 * gösterilir.
 *
 * `duzenlenebilir`: düzenleme ekranında "Belge Ekle" ve sayfa silme
 * (kullanıcı, 2026-10-01: "Bilnex'ten aldığımız fatura kayıtlarında
 * fotoğraf yok ama biz de yükleyemiyoruz"). Yükleme kayıttan BAĞIMSIZ,
 * anında yazılır — faturadaki "Kaydet"e bağlansaydı vazgeçilen düzenleme
 * yüklenen belgeyi de kaybettirirdi.
 *
 * Sayfalar JWT gerektirdiği için `<img src>` ile doğrudan çekilemez; blob
 * alınıp object URL üretilir. Uç FATURA yetkisiyle okur
 * (`/invoices/{id}/belge-sayfalari/{sira}`) — dosya kutusu yetkisi olmayan
 * muhasebeci de belgeyi görebilsin.
 */
export default function FaturaBelgesi({ invoiceId, imageData, duzenlenebilir = false }) {
  const { hasPermission } = useAuth();
  const [sayfalar, setSayfalar] = useState([]);
  const [belgeId, setBelgeId] = useState(null);
  const [aktif, setAktif] = useState(1);
  const [url, setUrl] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [buyut, setBuyut] = useState(false);
  const [tazele, setTazele] = useState(0);
  const [silinecek, setSilinecek] = useState(null);
  const dosyaRef = useRef(null);

  const ozetiIsle = (d) => {
    setSayfalar(d?.sayfalar || []);
    setBelgeId(d?.belge_id || null);
  };

  useEffect(() => {
    if (!invoiceId) return;
    let iptal = false;
    api.get(`/invoices/${invoiceId}/belge-sayfalari`)
      .then(r => { if (!iptal) ozetiIsle(r.data); })
      .catch(() => { if (!iptal) ozetiIsle(null); });
    return () => { iptal = true; };
  }, [invoiceId]);

  const aktifSayfa = sayfalar.find(s => s.sira === aktif);

  useEffect(() => {
    if (!belgeId || !sayfalar.length) { setUrl(null); return; }
    let iptal = false, olusan = null;
    setYukleniyor(true);
    api.get(`/invoices/${invoiceId}/belge-sayfalari/${aktif}`, { responseType: 'blob' })
      .then(r => {
        if (iptal) return;
        olusan = URL.createObjectURL(r.data);
        setUrl(olusan);
      })
      .catch(() => { if (!iptal) setUrl(null); })
      .finally(() => { if (!iptal) setYukleniyor(false); });
    return () => { iptal = true; if (olusan) URL.revokeObjectURL(olusan); };
  }, [invoiceId, belgeId, aktif, sayfalar.length, tazele]);

  // Döndürme KALICI (açı sunucuda, dosya kutusu ucu). Yan çekilmiş faturayı
  // buradan düzeltmek kutudaki kaydı da düzeltir — belge tek yerde duruyor.
  const dondurebilir = belgeId && hasPermission('dosya_kutusu', 'edit') && !pdfMi(aktifSayfa);
  const dondur = async (yon) => {
    const mevcut = aktifSayfa?.rotasyon || 0;
    const yeni = (mevcut + (yon === 'sag' ? 90 : 270)) % 360;
    try {
      await api.patch(`/dosya-kutusu/${belgeId}/sayfa/${aktif}/rotasyon`, { rotasyon: yeni });
      setSayfalar(prev => prev.map(s => (s.sira === aktif ? { ...s, rotasyon: yeni } : s)));
      setTazele(t => t + 1);
    } catch {
      toast.error('Döndürülemedi');
    }
  };

  const ekle = async (e) => {
    // `input.files` CANLI liste: sıfırlamadan ÖNCE kopyala (bkz. ERP.md)
    const secilen = Array.from(e.target.files || []);
    if (dosyaRef.current) dosyaRef.current.value = '';
    if (!secilen.length) return;
    const veri = new FormData();
    secilen.forEach(d => veri.append('dosyalar', d));
    setGonderiliyor(true);
    try {
      const r = await api.post(`/invoices/${invoiceId}/belge-sayfalari`, veri,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      ozetiIsle(r.data);
      setAktif((r.data?.sayfalar || []).length - (r.data?.eklenen || 1) + 1);
      setTazele(t => t + 1);
      toast.success(`${r.data?.eklenen || secilen.length} sayfa eklendi`);
      if ((r.data?.mukerrer_uyarisi || []).length) {
        const m = r.data.mukerrer_uyarisi[0];
        toast.warning('Aynı dosya sistemde başka bir belgede de var'
          + (m.islenen_kayit?.no ? ` (${m.islenen_kayit.no})` : ''), { duration: 8000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge yüklenemedi');
    } finally {
      setGonderiliyor(false);
    }
  };

  const sil = async () => {
    const sira = silinecek;
    setSilinecek(null);
    try {
      const r = await api.delete(`/invoices/${invoiceId}/belge-sayfalari/${sira}`);
      ozetiIsle(r.data);
      setAktif(a => Math.max(1, Math.min(a, (r.data?.sayfalar || []).length || 1)));
      setTazele(t => t + 1);
      toast.success('Sayfa kaldırıldı');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sayfa kaldırılamadı');
    }
  };

  const cokSayfa = sayfalar.length > 1;
  const gosterilen = url || (sayfalar.length ? null : imageData);
  const pdf = sayfalar.length > 0 && pdfMi(aktifSayfa);
  const ekleyebilir = duzenlenebilir && hasPermission('invoices', 'edit');
  const silebilir = duzenlenebilir && sayfalar.length > 0 && hasPermission('invoices', 'delete');
  if (!gosterilen && !yukleniyor && !ekleyebilir) return null;

  return (
    <>
      <Card className="erp-card mt-4" data-testid="fatura-belgesi">
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base font-semibold text-[#1a1916]">
            Fatura Belgesi
            {sayfalar.length > 0 && (
              <span className="ml-2 text-xs font-normal text-[#8a7e70]">{sayfalar.length} sayfa</span>
            )}
          </CardTitle>
          <div className="flex items-center gap-1 flex-wrap">
            {dondurebilir && (
              <>
                <Button variant="ghost" size="sm" onClick={() => dondur('sol')}
                  className="h-7 w-7 p-0 text-[#6b5f4f]" title="Sola döndür" data-testid="fatura-dondur-sol">
                  <RotateCcw size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => dondur('sag')}
                  className="h-7 w-7 p-0 text-[#6b5f4f] mr-1" title="Sağa döndür" data-testid="fatura-dondur-sag">
                  <RotateCw size={14} />
                </Button>
              </>
            )}
            {cokSayfa && (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={aktif <= 1}
                  onClick={() => setAktif(a => Math.max(1, a - 1))} className="h-7 w-7 p-0 text-[#6b5f4f]">
                  <ChevronLeft size={15} />
                </Button>
                <span className="text-xs text-[#6b5f4f] tabular-nums">{aktif} / {sayfalar.length}</span>
                <Button variant="ghost" size="sm" disabled={aktif >= sayfalar.length}
                  onClick={() => setAktif(a => Math.min(sayfalar.length, a + 1))} className="h-7 w-7 p-0 text-[#6b5f4f]">
                  <ChevronRight size={15} />
                </Button>
              </div>
            )}
            {silebilir && (
              <Button variant="ghost" size="sm" onClick={() => setSilinecek(aktif)}
                className="h-7 px-2 text-[#7a3535] hover:bg-[#7a3535]/10 text-xs" title="Bu sayfayı kaldır"
                data-testid="fatura-belge-sil">
                <Trash2 size={13} className="mr-1" /> Sayfayı kaldır
              </Button>
            )}
            {ekleyebilir && (
              <>
                <Button variant="outline" size="sm" disabled={gonderiliyor}
                  onClick={() => dosyaRef.current?.click()}
                  className="h-7 px-2.5 text-xs border-[#3d6e4e] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
                  data-testid="fatura-belge-ekle">
                  {gonderiliyor
                    ? <><Loader2 size={13} className="mr-1 animate-spin" /> Yükleniyor...</>
                    : <><Paperclip size={13} className="mr-1" /> Belge Ekle</>}
                </Button>
                <input ref={dosyaRef} type="file" multiple className="hidden"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/heic,application/pdf,.pdf"
                  onChange={ekle} data-testid="fatura-belge-dosya" />
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {yukleniyor && !gosterilen ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="animate-spin text-[#8a7e70]" size={20} />
            </div>
          ) : !gosterilen ? (
            <p className="text-xs text-[#8a7e70]" data-testid="fatura-belge-yok">
              Bu faturaya bağlı belge yok. Fotoğraf ya da PDF eklemek için "Belge Ekle"
              (birden çok dosya seçilebilir; her biri bir sayfa olur). Belge, faturayı
              kaydetmeyi beklemeden hemen saklanır.
            </p>
          ) : pdf ? (
            <div className="space-y-1.5">
              <iframe src={gosterilen} title={`Sayfa ${aktif}`}
                className="w-full h-[420px] rounded-lg border border-[#d0c4b0] bg-white" />
              <a href={gosterilen} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#3d6e4e] hover:underline">
                <FileText size={12} /> PDF'i yeni sekmede aç
              </a>
            </div>
          ) : (
            <div className="relative inline-block cursor-zoom-in"
              onClick={() => setBuyut(true)} data-testid="fatura-gorsel">
              <img src={gosterilen} alt={`Sayfa ${aktif}`}
                className="max-h-48 rounded-lg border border-[#d0c4b0] object-contain" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20 rounded-lg">
                <ZoomIn size={24} className="text-white drop-shadow" />
              </div>
            </div>
          )}
          {cokSayfa && (
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {sayfalar.map(sf => (
                <button key={sf.sira} type="button" onClick={() => setAktif(sf.sira)}
                  className={`text-[11px] px-2 h-6 rounded border transition-colors
                    ${aktif === sf.sira
                      ? 'bg-[#4a5a3a] text-white border-[#4a5a3a]'
                      : 'bg-white text-[#6b5f4f] border-[#d0c4b0] hover:bg-[#f5f1e8]'}`}
                  data-testid={`fatura-sayfa-${sf.sira}`}>
                  {sf.sira}. sayfa{pdfMi(sf) ? ' (PDF)' : ''}
                </button>
              ))}
            </div>
          )}
          {duzenlenebilir && !sayfalar.length && imageData && (
            <p className="mt-2 text-[11px] text-[#8a7e70]">
              Bu görsel faturanın eski kaydından. Eklediğiniz belge bunun yerine gösterilir.
            </p>
          )}
        </CardContent>
      </Card>

      {buyut && gosterilen && !pdf && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setBuyut(false)}>
          <button type="button" className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setBuyut(false)}>
            <X size={28} />
          </button>
          {cokSayfa && (
            <>
              <button type="button" className="absolute left-4 text-white hover:text-gray-300 disabled:opacity-30"
                disabled={aktif <= 1}
                onClick={(e) => { e.stopPropagation(); setAktif(a => Math.max(1, a - 1)); }}>
                <ChevronLeft size={34} />
              </button>
              <button type="button" className="absolute right-4 text-white hover:text-gray-300 disabled:opacity-30"
                disabled={aktif >= sayfalar.length}
                onClick={(e) => { e.stopPropagation(); setAktif(a => Math.min(sayfalar.length, a + 1)); }}>
                <ChevronRight size={34} />
              </button>
              <span className="absolute bottom-4 text-white text-sm tabular-nums">{aktif} / {sayfalar.length}</span>
            </>
          )}
          <img src={gosterilen} alt={`Sayfa ${aktif}`}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()} />
        </div>
      )}

      <AlertDialog open={silinecek != null} onOpenChange={(a) => !a && setSilinecek(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sayfa kaldırılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              {silinecek}. sayfa faturanın belgesinden kalıcı olarak silinir
              {sayfalar.length === 1 ? ' (son sayfa — belge kaydı da kalkar)' : ''}.
              Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={sil} className="bg-[#7a3535] hover:bg-[#5a2525]"
              data-testid="fatura-belge-sil-onay">Kaldır</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
